import { strToU8 } from "fflate";
import log, { LoggingLevel } from "../log.js";
import { Context } from "../context.js";
import { DELETION_REQUEST_PING_NAME } from "../constants.js";
import { isJSONValue, isNumber, isObject, isString } from "../utils.js";
export const PINGS_DATABASE_LOG_TAG = "core.Pings.Database";
class PingsDatabase {
    constructor() {
        this.store = new Context.platform.Storage("pings");
    }
    attachObserver(observer) {
        this.observer = observer;
    }
    recordPing(path, identifier, payload, headers) {
        const ping = {
            collectionDate: new Date().toISOString(),
            path,
            payload
        };
        if (headers) {
            ping.headers = headers;
        }
        this.store.update([identifier], () => ping);
        this.observer && this.observer.update(identifier, ping);
    }
    deletePing(identifier) {
        this.store.delete([identifier]);
    }
    getAllPings() {
        const allStoredPings = this.store.get();
        const finalPings = {};
        if (isObject(allStoredPings)) {
            for (const identifier in allStoredPings) {
                const ping = allStoredPings[identifier];
                if (isValidPingInternalRepresentation(ping)) {
                    finalPings[identifier] = ping;
                }
                else {
                    log(PINGS_DATABASE_LOG_TAG, `Unexpected data found in pings database: ${JSON.stringify(ping, null, 2)}. Deleting.`, LoggingLevel.Warn);
                    this.store.delete([identifier]);
                }
            }
        }
        return sortPings(finalPings);
    }
    scanPendingPings() {
        if (!this.observer) {
            return;
        }
        const pings = this.getAllPingsWithoutSurplus();
        for (const [identifier, ping] of pings) {
            this.observer.update(identifier, ping);
        }
    }
    clearAll() {
        this.store.delete([]);
    }
    getAllPingsWithoutSurplus(maxCount = 250, maxSize = 10 * 1024 * 1024) {
        const allPings = this.getAllPings();
        const pings = allPings
            .filter(([_, ping]) => !isDeletionRequest(ping))
            .reverse();
        const deletionRequestPings = allPings.filter(([_, ping]) => isDeletionRequest(ping));
        const total = pings.length;
        if (total > maxCount) {
            log(PINGS_DATABASE_LOG_TAG, [
                `More than ${maxCount} pending pings in the pings database,`,
                `will delete ${total - maxCount} old pings.`
            ], LoggingLevel.Warn);
        }
        let deleting = false;
        let pendingPingsCount = 0;
        let pendingPingsDatabaseSize = 0;
        const remainingPings = [];
        for (const [identifier, ping] of pings) {
            pendingPingsCount++;
            pendingPingsDatabaseSize += getPingSize(ping);
            if (!deleting && pendingPingsDatabaseSize > maxSize) {
                log(PINGS_DATABASE_LOG_TAG, [
                    `Pending pings database has reached the size quota of ${maxSize} bytes,`,
                    "outstanding pings will be deleted."
                ], LoggingLevel.Warn);
                deleting = true;
            }
            if (pendingPingsCount > maxCount) {
                deleting = true;
            }
            if (deleting) {
                this.deletePing(identifier);
            }
            else {
                remainingPings.unshift([identifier, ping]);
            }
        }
        return [...deletionRequestPings, ...remainingPings];
    }
}
export function isDeletionRequest(ping) {
    return ping.path.split("/")[3] === DELETION_REQUEST_PING_NAME;
}
export function getPingSize(ping) {
    return strToU8(JSON.stringify(ping)).length;
}
export function isValidPingInternalRepresentation(v) {
    if (isObject(v)) {
        const hasValidCollectionDate = "collectionDate" in v &&
            isString(v.collectionDate) &&
            isNumber(new Date(v.collectionDate).getTime());
        const hasValidPath = "path" in v && isString(v.path);
        const hasValidPayload = "payload" in v && isJSONValue(v.payload) && isObject(v.payload);
        const hasValidHeaders = !("headers" in v) || (isJSONValue(v.headers) && isObject(v.headers));
        if (!hasValidCollectionDate || !hasValidPath || !hasValidPayload || !hasValidHeaders) {
            return false;
        }
        return true;
    }
    return false;
}
export function sortPings(pings) {
    return Object.entries(pings).sort(([_idA, { collectionDate: dateA }], [_idB, { collectionDate: dateB }]) => {
        const timeA = new Date(dateA).getTime();
        const timeB = new Date(dateB).getTime();
        return timeA - timeB;
    });
}
export default PingsDatabase;
